/**
 * Plan 20a, Task 3.2 — Toast slide-in + scoreboard row tween hook
 *
 * Factory: createToastScoreboardSlideHook(config: UiVfxHookConfig)
 *   Returns a UiVfxHook<HTMLElement, { kind: 'toast-in' | 'toast-out' | 'row-update' }>
 *   with id 'toast-scoreboard-slide'.
 *
 * Supported kinds:
 *   toast-in   — xPercent 120→0 over normal (250ms) with bouncy easing. Per components.md §9.
 *   toast-out  — opacity 1→0 + xPercent 0→30 over fast (150ms) with snappy easing. Per §9.
 *   row-update — 3-tween pulse: scale 1.0→1.02→1.0 + box-shadow glow over ~150ms. Per §10.
 *
 * GSAP transform note (verified via Context7 GSAP docs):
 *   `xPercent` is the correct property for percentage-based translateX.
 *   `x` uses pixel units. `xPercent: 120` maps to translateX(120%).
 *   Source: https://gsap.com/docs/v3/GSAP/CorePlugins/CSS (transforms section)
 *
 * Box-shadow glow for row-update:
 *   Tweens from 'none' to '0 0 10px 2px #FFD700' (gold glow) then back to 'none'
 *   using gsap's boxShadow property (complex string tweening supported per GSAP docs).
 *   The glow value '0 0 10px 2px #FFD700' (gold, 10px blur, 2px spread) conveys
 *   a positive score/rank-update event; consumer CSS may override via CSS custom property.
 *
 * Duration / easing tokens are read from data-vfx-* attributes on the target element
 * with hardcoded fallbacks so the hook compiles standalone without a token resolver:
 *   data-vfx-duration-normal → normalMs   fallback 250ms (toast-in)
 *   data-vfx-duration-fast   → fastMs     fallback 150ms (toast-out, row-update)
 *   data-vfx-easing-bouncy   → bouncyEase fallback 'back.out(2.5)'
 *   data-vfx-easing-snappy   → snappyEase fallback 'power2.out'
 *
 * NOTE — completion callbacks:
 *   The gsap-registry sentinel tween extends every timeline to VIRTUAL_DURATION=3600s.
 *   eventCallback('onComplete') on the root timeline fires at 3600s, not at the natural
 *   animation end. Use timeline.call(fn, undefined, absoluteSeconds) instead.
 *   Canonical reference: src/client/renderer/animation/hit-reaction.ts:17-18.
 */

import type { UiVfxHook, UiVfxHookConfig } from './types';
import type { GsapTimelineHandle } from '../../renderer/vfx/gsap-registry';

// ─────────────────────────────────────────────────────────────────────────────
// Public opts type
// ─────────────────────────────────────────────────────────────────────────────

export interface ToastScoreboardSlideOpts {
  kind: 'toast-in' | 'toast-out' | 'row-update';
}

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Gold glow box-shadow applied during row-update pulse.
 * Conveys a positive score / rank-update event.
 * Consumer CSS may override via CSS custom property if needed.
 */
const ROW_GLOW_SHADOW = '0 0 10px 2px #FFD700';

/** No box-shadow — start/end state for row-update glow tween. */
const ROW_NO_SHADOW = '0 0 0px 0px rgba(255,215,0,0)';

// ─────────────────────────────────────────────────────────────────────────────
// Internal helpers — token reading (same pattern as sibling hooks)
// ─────────────────────────────────────────────────────────────────────────────

/** Reads a millisecond duration from a data-vfx-* attribute; fallback in ms. */
function readDurationMs(el: HTMLElement, attr: string, fallbackMs: number): number {
  const raw = el.dataset[attr];
  if (raw) {
    const parsed = parseFloat(raw);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }
  return fallbackMs;
}

/** Reads an easing string from a data-vfx-* attribute; falls back to provided default. */
function readEasing(el: HTMLElement, attr: string, fallback: string): string {
  const raw = el.dataset[attr];
  return raw && raw.trim().length > 0 ? raw.trim() : fallback;
}

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export function createToastScoreboardSlideHook(
  config: UiVfxHookConfig,
): UiVfxHook<HTMLElement, ToastScoreboardSlideOpts> {
  const { gsapRegistry } = config;

  /** In-flight handle; at most one active at a time. */
  let currentHandle: GsapTimelineHandle | null = null;

  /** Kill the current in-flight timeline, if any. */
  function killCurrent(): void {
    if (currentHandle) {
      currentHandle.dispose();
      currentHandle = null;
    }
  }

  function play(target: HTMLElement, opts?: ToastScoreboardSlideOpts): GsapTimelineHandle {
    // Kill any prior timeline before spawning a new one.
    killCurrent();

    const kind = opts?.kind ?? 'toast-in';

    // ── Read timing tokens from element data-attributes (with fallbacks) ──────
    // data-vfx-duration-normal → dataset.vfxDurationNormal → 250ms
    // data-vfx-duration-fast   → dataset.vfxDurationFast   → 150ms
    const normalMs = readDurationMs(target, 'vfxDurationNormal', 250);
    const fastMs = readDurationMs(target, 'vfxDurationFast', 150);

    // data-vfx-easing-bouncy → dataset.vfxEasingBouncy → 'back.out(2.5)'
    // data-vfx-easing-snappy → dataset.vfxEasingSnappy → 'power2.out'
    const bouncyEase = readEasing(target, 'vfxEasingBouncy', 'back.out(2.5)');
    const snappyEase = readEasing(target, 'vfxEasingSnappy', 'power2.out');

    const normalSec = normalMs / 1000;
    const fastSec = fastMs / 1000;

    const handle = gsapRegistry.create(`toast-scoreboard-slide:${kind}`);
    const tl = handle.timeline;

    switch (kind) {
      case 'toast-in': {
        // Slide in from the right: xPercent 120→0 over normal (250ms) with bouncy easing.
        // xPercent is GSAP's shorthand for percentage-based translateX.
        // xPercent: 120 ≡ translateX(120%).
        tl.fromTo(
          target,
          { xPercent: 120 },
          { xPercent: 0, duration: normalSec, ease: bouncyEase },
          0,
        );
        break;
      }

      case 'toast-out': {
        // Slide out to the right while fading: opacity 1→0 + xPercent 0→30 over fast (150ms).
        // Single tween with two properties — efficient and keeps them in sync.
        tl.to(
          target,
          {
            opacity: 0,
            xPercent: 30,
            duration: fastSec,
            ease: snappyEase,
          },
          0,
        );
        break;
      }

      case 'row-update': {
        // 3-tween pulse over ~150ms total:
        //   Tween 1 (0–75ms):  scale 1.0 → 1.02 + box-shadow 'none' → glow
        //   Tween 2 (75–150ms): scale 1.02 → 1.0
        //   Tween 3 (75–150ms): box-shadow glow → 'none' (overlapping with tween 2)
        // All positions are absolute (0 and halfSec) to avoid the sentinel-append bug.
        // Half duration = fast/2 = 75ms; giving a 75 + 75ms = 150ms total (scale).
        // The shadow tween runs from the same offset as scale-down, both completing at ~150ms.
        const halfSec = fastSec / 2; // 75ms

        // Tween 1: scale up + shadow in (0 → 75ms)
        tl.fromTo(
          target,
          { scale: 1.0, boxShadow: ROW_NO_SHADOW },
          { scale: 1.02, boxShadow: ROW_GLOW_SHADOW, duration: halfSec, ease: 'power2.out' },
          0,
        );

        // Tween 2: scale down (75ms → 150ms)
        tl.to(
          target,
          {
            scale: 1.0,
            duration: halfSec,
            ease: 'power2.in',
          },
          halfSec,
        );

        // Tween 3: shadow out, positioned at 75ms mark (same start as tween 2)
        tl.to(
          target,
          {
            boxShadow: ROW_NO_SHADOW,
            duration: halfSec,
            ease: 'power2.in',
          },
          halfSec, // Absolute position: same 75ms start as tween 2
        );

        break;
      }
    }

    currentHandle = handle;
    return handle;
  }

  function dispose(): void {
    killCurrent();
  }

  return {
    id: 'toast-scoreboard-slide',
    play,
    dispose,
  };
}
